import cx from 'classnames'
import Image, { getImageProps } from 'next/image'
import { useTranslations } from 'next-intl'

import BrawlFlare from '@/assets/images/storefronts/brawlstars/rays.png'
import CompleteCheckMark from '@/assets/images/storefronts/clashroyale/bonus-complete-checkmark.png'
import IncompleteCheckMark from '@/assets/images/storefronts/clashroyale/bonus-incomplete-checkmark.png'
import { getBackgroundImage } from '@/features/storefronts/_storeBase/assetRendering/imageUtils'
import { PointsTag } from '@/features/storefronts/_storeBase/card/v2/shared/Tags/components/PointsTag'
import { StoreTypography } from '@/features/storefronts/_storeBase/typography/StoreTypography'
import { useGameSlug } from '@/features/storefronts/game-provider'

import styles from './bonusMissionItem.module.scss'

export type CRStoreMissionType = {
  missionId: string
  title: string
  isToday: boolean
  isCompleted: boolean
  rewardPoints: number
}

interface BonusMissionProps {
  mission: CRStoreMissionType
}

export const CRBonusMissionItem = ({ mission }: BonusMissionProps) => {
  const t = useTranslations()
  const gameSlug = useGameSlug()

  if (!mission) {
    return null
  }

  const { rewardPoints, title } = mission
  const isComplete = mission.isCompleted
  const helpSubTitle = isComplete ? t('mission_completed') : t('mission_tasks_info')

  const {
    props: { srcSet },
  } = getImageProps({ alt: '', width: 400, height: 400, src: BrawlFlare })
  const backgroundImage = getBackgroundImage(srcSet)

  return (
    <div
      className={cx(styles.item, styles[gameSlug], {
        [styles.completed]: isComplete,
      })}
    >
      <div className={styles.content}>
        <div className={styles.detailsContainer}>
          <div className={styles.textGroup}>
            {isComplete && (
              <StoreTypography
                className={styles.compltetedText}
                tag="span"
                textSize="sm"
                style="plain"
                overrideFont="supercellText"
              >
                {t('mission_completed')}!
              </StoreTypography>
            )}
            <StoreTypography
              className={styles.title}
              tag="span"
              textSize="md"
              overrideFont="supercellHeadline"
              style="plain"
            >
              {mission.isToday ? t('mission_today') : title}
            </StoreTypography>

            {!isComplete && (
              <StoreTypography
                tag="span"
                textSize="sm"
                style="plain"
                overrideFont="supercellText"
                className={styles.subtitle}
              >
                {helpSubTitle}
              </StoreTypography>
            )}
          </div>
        </div>

        <div className={styles.missionItemContainer}>
          <div className={styles.imageContainer}>
            <div className={styles.flareContainer} style={{ backgroundImage: backgroundImage }} />

            <div className={styles.missionItemImage}>
              <div className={styles.imageWrapper}>
                <div className={styles.completedIconContainer}>
                  {isComplete ? (
                    <Image src={CompleteCheckMark} alt={t('mission_completed')} width={48} height={42} />
                  ) : (
                    <Image src={IncompleteCheckMark} alt={t('mission_completed')} width={48} height={42} />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className={cx(styles.pointsContainer, { [styles.completed]: isComplete })}>
            <PointsTag
              points={rewardPoints}
              variant={isComplete ? 'BonusDark' : 'Bonus'}
              tagIcon={isComplete ? 'Points' : 'Mission'}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

CRBonusMissionItem.displayName = 'CRBonusMissionItem'
